import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AppVersionService } from "../services/appVersion.service";
import {
  CreateAppVersionPayload,
  UpdateAppVersionPayload,
  GetAppVersionsParams,
  CurrentAppVersion,
} from "../domain/appVersion.interface";

const QUERY_KEY = "app-versions";

export const useGetAppVersions = (params?: GetAppVersionsParams) => {
  const { data, isLoading, refetch, error } = useQuery({
    queryKey: [QUERY_KEY, params],
    queryFn: () => AppVersionService.findAll(params),
  });

  return {
    appVersions: data || [],
    isLoadingAppVersions: isLoading,
    refetchAppVersions: refetch,
    error,
  };
};

export const useGetCurrentAppVersion = (platform: string) => {
  const { data, isLoading, refetch } = useQuery<CurrentAppVersion>({
    queryKey: [QUERY_KEY, "current", platform],
    queryFn: () => AppVersionService.findCurrent(platform),
    enabled: !!platform,
  });

  return {
    appVersion: data,
    isLoadingAppVersion: isLoading,
    refetchAppVersion: refetch,
  };
};

export const useCreateAppVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateAppVersionPayload) =>
      AppVersionService.create(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateAppVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appVersionId,
      payload,
    }: {
      appVersionId: string;
      payload: UpdateAppVersionPayload;
    }) => AppVersionService.update(appVersionId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteAppVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (appVersionId: string) =>
      AppVersionService.softDelete(appVersionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useSetCurrentAppVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      appVersionId,
      platform,
    }: {
      appVersionId: string;
      platform: string;
    }) => AppVersionService.setCurrent(appVersionId, platform),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};
